export type Lang = 'en' | 'es'

export const translations = {
  en: {
    meta: {
      home: {
        title:
          'MDA Production & Process Solutions — Warehouse & Manufacturing Consulting',
        description:
          'Engineering-driven solutions for manufacturing and warehouse operations focused on productivity, efficiency, and continuous improvement.',
      },
      services: {
        title: 'Services — MDA Production & Process Solutions',
        description:
          'Warehouse consulting services: design & layout, operations improvement, audits, inventory management, implementation, and data & analytics.',
      },
      about: {
        title: 'About — MDA Production & Process Solutions',
        description:
          'MDA is a consulting firm built around one idea: manufacturing and warehouse operations should be engineered, not improvised.',
      },
      contact: {
        title: 'Contact — MDA Production & Process Solutions',
        description:
          "Contact us directly. We'll review your operation and respond with next steps.",
      },
    },
    nav: {
      home: 'Home',
      services: 'Services',
      about: 'About',
      contact: 'Contact',
      requestConsulting: 'Request Consulting',
    },
    common: {
      requestConsulting: 'Request Consulting',
      viewServices: 'View Services',
      howCanWeHelp: 'How can we help?',
      ctaSubtitle:
        "Contact us directly. We'll review your operation and respond with next steps.",
    },
    hero: {
      eyebrow: 'Production & Process Solutions',
      title: [
        'Engineering driven',
        'solutions for',
        'manufacturing',
        'and warehouse',
        'operations',
      ],
      subtitle:
        'We help manufacturing businesses improve operations through engineering-driven solutions focused on productivity, efficiency, and continuous improvement.',
      stats: [
        { value: '+30%', label: ['Average efficiency gain'] },
        { value: '99.2%', label: ['Inventory accuracy', 'achieved'] },
        { value: '40+', label: ['Projects delivered'] },
      ],
    },
    stats: {
      heading: 'Real engineering, measurable results',
      items: [
        { value: '+30%', label: 'Average efficiency gain' },
        { value: '+25%', label: 'Productivity increase' },
        { value: '99.2%', label: 'Inventory accuracy achieved' },
        { value: '40+', label: 'Projects delivered' },
      ],
    },
    servicesPreview: {
      eyebrow: 'Warehouse Consulting Services',
      heading: [
        'Practical solutions to optimize',
        'your warehouse operations',
        'and drive measurable results.',
      ],
      cards: [
        {
          title: 'WAREHOUSE DESIGN & LAYOUT',
          description: 'Optimize space and flow for maximum efficiency.',
        },
        {
          title: 'OPERATIONS IMPROVEMENT',
          description:
            'Streamline processes to increase productivity and reduce costs.',
        },
        {
          title: 'OPERATIONS AUDIT',
          description:
            'Identify gaps and opportunities to improve performance.',
        },
        {
          title: 'INVENTORY MANAGEMENT',
          description: 'Improve accuracy, visibility and control.',
        },
        {
          title: 'IMPLEMENTATION & PROJECTS',
          description:
            'Plan and execute solutions that deliver lasting results.',
        },
        {
          title: 'DATA & ANALYTICS',
          description:
            'Use data to make better decisions and drive continuous improvement.',
        },
      ],
    },
    testimonials: {
      heading: 'What our clients say',
      subtitle:
        "Real-world results from operations leaders we've partnered with.",
      items: [
        {
          quote:
            'MDA redesigned our warehouse layout and we cut picking time by 32% in the first quarter.',
          initials: 'OD',
          role: 'Operations Director, Food & Beverage Distributor',
        },
        {
          quote:
            "Their operations audit uncovered process gaps we'd missed for years. Real engineering, real results.",
          initials: 'PM',
          role: 'Plant Manager, Auto Parts Manufacturer',
        },
        {
          quote:
            "Inventory accuracy went from 87% to 99.2% after implementing MDA's recommendations.",
          initials: 'SC',
          role: 'Supply Chain Lead, 3PL Provider',
        },
        {
          quote:
            'Practical, hands-on, and data-driven — exactly what our operation needed.',
          initials: 'CC',
          role: 'COO, Consumer Goods Company',
        },
      ],
    },
    contactCta: {
      benefits: [
        'Productivity',
        'Efficiency',
        'Continuous improvement',
        'Engineering-driven',
      ],
    },
    footer: {
      tagline: 'Engineering-driven warehouse and manufacturing consulting.',
      servicesHeading: 'Services',
      contactHeading: 'Contact',
      services: [
        'Warehouse Design & Layout',
        'Operations Improvement',
        'Operations Audit',
        'Inventory Management',
        'Implementation & Projects',
        'Data & Analytics',
      ],
      location: ['Manufacturing & Warehouse', 'Consulting'],
      copyright:
        '© 2026 MDA Production & Process Solutions. All rights reserved.',
      signoff: 'Engineered with care.',
    },
    servicesPage: {
      eyebrow: 'Warehouse Consulting Services',
      heading:
        'Practical solutions to optimize your warehouse operations and drive measurable results.',
      cards: [
        {
          title: 'Warehouse Design & Layout',
          subtitle: 'Optimize space and flow for maximum efficiency.',
          description:
            'Slotting strategies, rack configuration, dock-to-stock flow, and CAD-based layouts engineered to remove bottlenecks and maximize cube utilization.',
        },
        {
          title: 'Operations Improvement',
          subtitle:
            'Streamline processes to increase productivity and reduce costs.',
          description:
            'Lean methodologies, standard work, labor balancing, and KPI dashboards that turn daily operations into a predictable, measurable system.',
        },
        {
          title: 'Operations Audit',
          subtitle: 'Identify gaps and opportunities to improve performance.',
          description:
            'On-site diagnostic of processes, people, and systems — followed by a prioritized roadmap with quantified savings and quick wins.',
        },
        {
          title: 'Inventory Management',
          subtitle: 'Improve accuracy, visibility and control.',
          description:
            'Cycle counting programs, ABC classification, WMS configuration, and inventory policies that lift accuracy above 99% and free up working capital.',
        },
        {
          title: 'Implementation & Projects',
          subtitle: 'Plan and execute solutions that deliver lasting results.',
          description:
            "Project management, change management, and on-the-floor execution to ensure recommendations don't stay in a slide deck — they go live and stick.",
        },
        {
          title: 'Data & Analytics',
          subtitle:
            'Use data to make better decisions and drive continuous improvement.',
          description:
            'Operational dashboards, KPI architecture, and analytics models that turn raw warehouse data into clear, actionable decisions.',
        },
      ],
      ctaBenefits: ['Productivity', 'Efficiency', 'Continuous improvement'],
    },
    aboutPage: {
      heroTitle: 'About MDA',
      heroSubtitle:
        'MDA Production & Process Solutions is a consulting firm built around one idea: manufacturing and warehouse operations should be engineered, not improvised.',
      story: [
        'We combine industrial engineering fundamentals, lean methodologies, and data analytics to deliver practical solutions that move the numbers — picking time, throughput, accuracy, cost per unit.',
        'Every engagement starts on the warehouse floor and ends with a measurable, sustainable outcome. No vague slide decks, no theoretical frameworks — just engineering applied to real operations.',
      ],
      testimonialQuote:
        '"Their operations audit uncovered process gaps we\'d missed for years. Real engineering, real results."',
      testimonialAuthor: '— Plant Manager, Auto Parts Manufacturer',
      valuesHeading: 'Our values',
      values: [
        {
          title: 'Engineering first',
          description:
            'Every recommendation is grounded in industrial engineering fundamentals, not guesswork.',
        },
        {
          title: 'Built with purpose',
          description:
            'We design every solution around a specific operational goal and a clear purpose.',
        },
        {
          title: 'Continuous improvement',
          description:
            'We leave teams with systems that keep getting better long after we are gone.',
        },
      ],
    },
    contactPage: {
      heroTitle: 'How can we help?',
      heroSubtitle:
        "Contact us directly. We'll review your operation and respond with next steps.",
      labels: {
        fullName: 'Full name',
        company: 'Company',
        email: 'Work email',
        phone: 'Phone (optional)',
        service: 'Select a service',
        message: 'How can we help?',
      },
      placeholders: {
        fullName: 'Jane Doe',
        company: 'Acme Logistics',
        email: 'jane@company.com',
        phone: '+1 (555) 000-0000',
        message: 'Tell us about your operation and what you want to improve…',
      },
      serviceDefault: 'Select a service...',
      serviceOptions: [
        'Warehouse Design & Layout',
        'Operations Improvement',
        'Operations Audit',
        'Inventory Management',
        'Implementation & Projects',
        'Data & Analytics',
      ],
      submit: 'Send request',
      sending: 'Sending…',
      emailDirect: 'Or email us directly:',
      validation: {
        fullName: 'Please enter your full name.',
        company: 'Please enter your company.',
        email: 'Please enter your work email.',
        emailInvalid: 'Please enter a valid email address.',
        service: 'Please select a service.',
        message: 'Please tell us how we can help.',
      },
      success:
        'Thank you! Your request has been submitted successfully. Our team will contact you shortly.',
      error: 'Something went wrong. Please try again or email us directly.',
    },
  },

  es: {
    meta: {
      home: {
        title:
          'MDA Production & Process Solutions — Consultoría de Almacenes y Manufactura',
        description:
          'Soluciones de ingeniería para operaciones de manufactura y almacenes enfocadas en productividad, eficiencia y mejora continua.',
      },
      services: {
        title: 'Servicios — MDA Production & Process Solutions',
        description:
          'Servicios de consultoría de almacenes: diseño y distribución, mejora de operaciones, auditorías, gestión de inventario, implementación y datos y analítica.',
      },
      about: {
        title: 'Nosotros — MDA Production & Process Solutions',
        description:
          'MDA es una firma de consultoría construida sobre una idea: las operaciones de manufactura y almacenes deben diseñarse con ingeniería, no improvisarse.',
      },
      contact: {
        title: 'Contacto — MDA Production & Process Solutions',
        description:
          'Contáctanos directamente. Revisaremos tu operación y te responderemos con los siguientes pasos.',
      },
    },
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      about: 'Nosotros',
      contact: 'Contacto',
      requestConsulting: 'Solicitar Consultoría',
    },
    common: {
      requestConsulting: 'Solicitar Consultoría',
      viewServices: 'Ver Servicios',
      howCanWeHelp: '¿Cómo podemos ayudar?',
      ctaSubtitle:
        'Contáctanos directamente. Revisaremos tu operación y te responderemos con los siguientes pasos.',
    },
    hero: {
      eyebrow: 'Soluciones de Producción y Procesos',
      title: [
        'Soluciones de',
        'ingeniería para',
        'operaciones de',
        'manufactura y',
        'almacenes',
      ],
      subtitle:
        'Ayudamos a las empresas de manufactura a mejorar sus operaciones mediante soluciones de ingeniería enfocadas en la productividad, la eficiencia y la mejora continua.',
      stats: [
        { value: '+30%', label: ['Ganancia media de eficiencia'] },
        { value: '99.2%', label: ['Precisión de inventario', 'alcanzada'] },
        { value: '40+', label: ['Proyectos entregados'] },
      ],
    },
    stats: {
      heading: 'Ingeniería real, resultados medibles',
      items: [
        { value: '+30%', label: 'Ganancia media de eficiencia' },
        { value: '+25%', label: 'Aumento de productividad' },
        { value: '99.2%', label: 'Precisión de inventario alcanzada' },
        { value: '40+', label: 'Proyectos entregados' },
      ],
    },
    servicesPreview: {
      eyebrow: 'Servicios de Consultoría de Almacenes',
      heading: [
        'Soluciones prácticas para optimizar',
        'tus operaciones de almacén',
        'y generar resultados medibles.',
      ],
      cards: [
        {
          title: 'DISEÑO Y DISTRIBUCIÓN DE ALMACENES',
          description:
            'Optimiza el espacio y el flujo para una máxima eficiencia.',
        },
        {
          title: 'MEJORA DE OPERACIONES',
          description:
            'Agiliza los procesos para aumentar la productividad y reducir costos.',
        },
        {
          title: 'AUDITORÍA DE OPERACIONES',
          description:
            'Identifica brechas y oportunidades para mejorar el desempeño.',
        },
        {
          title: 'GESTIÓN DE INVENTARIO',
          description: 'Mejora la precisión, la visibilidad y el control.',
        },
        {
          title: 'IMPLEMENTACIÓN Y PROYECTOS',
          description:
            'Planifica y ejecuta soluciones que generan resultados duraderos.',
        },
        {
          title: 'DATOS Y ANALÍTICA',
          description:
            'Usa los datos para tomar mejores decisiones e impulsar la mejora continua.',
        },
      ],
    },
    testimonials: {
      heading: 'Lo que dicen nuestros clientes',
      subtitle:
        'Resultados reales de líderes de operaciones con los que hemos trabajado.',
      items: [
        {
          quote:
            'MDA rediseñó la distribución de nuestro almacén y redujimos el tiempo de preparación en un 32% en el primer trimestre.',
          initials: 'OD',
          role: 'Director de Operaciones, Distribuidor de Alimentos y Bebidas',
        },
        {
          quote:
            'Su auditoría de operaciones reveló brechas de procesos que habíamos pasado por alto durante años. Ingeniería real, resultados reales.',
          initials: 'PM',
          role: 'Gerente de Planta, Fabricante de Autopartes',
        },
        {
          quote:
            'La precisión del inventario pasó del 87% al 99.2% tras implementar las recomendaciones de MDA.',
          initials: 'SC',
          role: 'Líder de Cadena de Suministro, Operador Logístico 3PL',
        },
        {
          quote:
            'Práctico, cercano y basado en datos: exactamente lo que nuestra operación necesitaba.',
          initials: 'CC',
          role: 'Director de Operaciones, Empresa de Bienes de Consumo',
        },
      ],
    },
    contactCta: {
      benefits: [
        'Productividad',
        'Eficiencia',
        'Mejora continua',
        'Basado en ingeniería',
      ],
    },
    footer: {
      tagline: 'Consultoría de almacenes y manufactura basada en ingeniería.',
      servicesHeading: 'Servicios',
      contactHeading: 'Contacto',
      services: [
        'Diseño y Distribución de Almacenes',
        'Mejora de Operaciones',
        'Auditoría de Operaciones',
        'Gestión de Inventario',
        'Implementación y Proyectos',
        'Datos y Analítica',
      ],
      location: ['Consultoría de Manufactura', 'y Almacenes'],
      copyright:
        '© 2026 MDA Production & Process Solutions. Todos los derechos reservados.',
      signoff: 'Hecho con ingeniería y cuidado.',
    },
    servicesPage: {
      eyebrow: 'Servicios de Consultoría de Almacenes',
      heading:
        'Soluciones prácticas para optimizar tus operaciones de almacén y generar resultados medibles.',
      cards: [
        {
          title: 'Diseño y Distribución de Almacenes',
          subtitle:
            'Optimiza el espacio y el flujo para una máxima eficiencia.',
          description:
            'Estrategias de ubicación, configuración de racks, flujo de muelle a estantería y distribuciones basadas en CAD diseñadas para eliminar cuellos de botella y maximizar el aprovechamiento del espacio.',
        },
        {
          title: 'Mejora de Operaciones',
          subtitle:
            'Agiliza los procesos para aumentar la productividad y reducir costos.',
          description:
            'Metodologías lean, trabajo estandarizado, balanceo de personal y tableros de KPI que convierten las operaciones diarias en un sistema predecible y medible.',
        },
        {
          title: 'Auditoría de Operaciones',
          subtitle:
            'Identifica brechas y oportunidades para mejorar el desempeño.',
          description:
            'Diagnóstico en sitio de procesos, personas y sistemas, seguido de una hoja de ruta priorizada con ahorros cuantificados y logros rápidos.',
        },
        {
          title: 'Gestión de Inventario',
          subtitle: 'Mejora la precisión, la visibilidad y el control.',
          description:
            'Programas de conteo cíclico, clasificación ABC, configuración de WMS y políticas de inventario que elevan la precisión por encima del 99% y liberan capital de trabajo.',
        },
        {
          title: 'Implementación y Proyectos',
          subtitle:
            'Planifica y ejecuta soluciones que generan resultados duraderos.',
          description:
            'Gestión de proyectos, gestión del cambio y ejecución en piso para asegurar que las recomendaciones no se queden en una presentación: se ponen en marcha y perduran.',
        },
        {
          title: 'Datos y Analítica',
          subtitle:
            'Usa los datos para tomar mejores decisiones e impulsar la mejora continua.',
          description:
            'Tableros operativos, arquitectura de KPI y modelos analíticos que transforman los datos del almacén en decisiones claras y accionables.',
        },
      ],
      ctaBenefits: ['Productividad', 'Eficiencia', 'Mejora continua'],
    },
    aboutPage: {
      heroTitle: 'Acerca de MDA',
      heroSubtitle:
        'MDA Production & Process Solutions es una firma de consultoría construida sobre una idea: las operaciones de manufactura y almacenes deben diseñarse con ingeniería, no improvisarse.',
      story: [
        'Combinamos los fundamentos de la ingeniería industrial, las metodologías lean y la analítica de datos para ofrecer soluciones prácticas que mueven los números: tiempo de preparación, rendimiento, precisión y costo por unidad.',
        'Cada proyecto comienza en el piso del almacén y termina con un resultado medible y sostenible. Sin presentaciones vagas ni marcos teóricos: solo ingeniería aplicada a operaciones reales.',
      ],
      testimonialQuote:
        '«Su auditoría de operaciones reveló brechas de procesos que habíamos pasado por alto durante años. Ingeniería real, resultados reales.»',
      testimonialAuthor: '— Gerente de Planta, Fabricante de Autopartes',
      valuesHeading: 'Nuestros valores',
      values: [
        {
          title: 'Ingeniería primero',
          description:
            'Cada recomendación se basa en los fundamentos de la ingeniería industrial, no en suposiciones.',
        },
        {
          title: 'Diseñado con propósito',
          description:
            'Diseñamos cada solución en torno a un objetivo operativo específico y un propósito claro.',
        },
        {
          title: 'Mejora continua',
          description:
            'Dejamos a los equipos con sistemas que siguen mejorando mucho después de que nos vamos.',
        },
      ],
    },
    contactPage: {
      heroTitle: '¿Cómo podemos ayudar?',
      heroSubtitle:
        'Contáctanos directamente. Revisaremos tu operación y te responderemos con los siguientes pasos.',
      labels: {
        fullName: 'Nombre completo',
        company: 'Empresa',
        email: 'Correo de trabajo',
        phone: 'Teléfono (opcional)',
        service: 'Selecciona un servicio',
        message: '¿Cómo podemos ayudar?',
      },
      placeholders: {
        fullName: 'Juan Pérez',
        company: 'Logística Acme',
        email: 'juan@empresa.com',
        phone: '+1 (555) 000-0000',
        message: 'Cuéntanos sobre tu operación y qué quieres mejorar…',
      },
      serviceDefault: 'Selecciona un servicio...',
      serviceOptions: [
        'Diseño y Distribución de Almacenes',
        'Mejora de Operaciones',
        'Auditoría de Operaciones',
        'Gestión de Inventario',
        'Implementación y Proyectos',
        'Datos y Analítica',
      ],
      submit: 'Enviar Solicitud',
      sending: 'Enviando…',
      emailDirect: 'O escríbenos directamente:',
      validation: {
        fullName: 'Por favor ingresa tu nombre completo.',
        company: 'Por favor ingresa tu empresa.',
        email: 'Por favor ingresa tu correo de trabajo.',
        emailInvalid: 'Por favor ingresa un correo electrónico válido.',
        service: 'Por favor selecciona un servicio.',
        message: 'Por favor cuéntanos cómo podemos ayudar.',
      },
      success:
        '¡Gracias! Tu solicitud ha sido enviada correctamente. Nuestro equipo se pondrá en contacto contigo en breve.',
      error: 'Algo salió mal. Inténtalo de nuevo o escríbenos directamente.',
    },
  },
} as const

export type Translations = (typeof translations)['en']
